import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Heading } from '@americanexpress/dls-react';
import { useRoomsContext } from '../../contexts/roomsContext';
import RemoveRoomButton from '../RemoveRoomButton/RemoveRoomButton';
import ChildrenAges from '../ChildrenAges/ChildrenAges';
import bindRoomIndexToUpdateTravelersUtil from '../../utils/bindRoomIndexToUpdateTravelersUtil';
import StepperRow from '../StepperRow/StepperRow';
import Infants from '../ChildrenAges/Infants';
import ChildAgeNote from '../ChildrenAges/ChildAgeNote';
import {
  DEFAULT_ADULT_TRAVELING,
  DEFAULT_CHILDREN_TRAVELING,
  DEFAULT_INFANTS_TRAVELING,
  MAX_PETS,
  MIN_ADULT_TRAVELING,
  MIN_CHILDREN_TRAVELING,
  MIN_INFANTS_TRAVELING,
  MIN_PETS,
} from '../../constants/travelersConstants';
import { useConfigContext } from '../../contexts/configContext';
import { useChildAgeRange } from '../../hooks/useChildAgeRange';

const Room = memo(({ isMultiRoom, canRemove, roomIndex }) => {
  const {
    rooms,
    updateAdults,
    updateChildCount,
    updateInfantCount,
    updatePets,
  } = useRoomsContext();

  const {
    hasAdultAgesHint,
    hasChildrenAges,
    hasInfants,
    hasInfantsAges,
    hasPets,
    hasRooms,
    maxAdults,
    maxChildren,
    maxCombinedChildrenAndInfants,
    maxInfants,
    maxTravelersCap,
  } = useConfigContext();

  const { formatMessage } = useIntl();

  const {
    adults, children, infants, pets,
  } = rooms[roomIndex];

  const roomId = roomIndex + 1;

  const groupHeadingLabel = formatMessage({ id: 'roomNumber' }, { roomId });
  const globalisedAdultAria = formatMessage({ id: 'adult' });
  const globalisedChildAria = formatMessage({ id: 'child' });
  const globalisedInfantAria = formatMessage({ id: 'infant' });

  const maxAdultsForStepper = maxTravelersCap
    ? maxTravelersCap - children.length - infants.length
    : maxAdults;

  const maxChildrenForStepper = useMemo(() => {
    const valueForChildrenUsingMaxTravelerCap = maxTravelersCap
      && maxTravelersCap - adults - infants.length;

    const valueForChildrenUsingMaxCombinedChildrenAndInfants = maxCombinedChildrenAndInfants
      && maxCombinedChildrenAndInfants - infants.length;

    if (valueForChildrenUsingMaxCombinedChildrenAndInfants !== undefined) {
      return valueForChildrenUsingMaxCombinedChildrenAndInfants;
    }

    if (valueForChildrenUsingMaxTravelerCap !== undefined) {
      return valueForChildrenUsingMaxTravelerCap;
    }

    return maxChildren;
  }, [
    adults,
    infants,
    maxChildren,
    maxTravelersCap,
    maxCombinedChildrenAndInfants,
  ]);

  const maxInfantsForStepper = useMemo(() => {
    const valueForInfantsUsingMaxTravelerCap = maxTravelersCap
      && maxTravelersCap - adults - children.length;

    const valueForInfantsUsingMaxCombinedChildrenAndInfants = maxCombinedChildrenAndInfants
      && maxCombinedChildrenAndInfants - children.length;

    if (valueForInfantsUsingMaxCombinedChildrenAndInfants !== undefined) {
      return valueForInfantsUsingMaxCombinedChildrenAndInfants;
    }
    if (valueForInfantsUsingMaxTravelerCap !== undefined) {
      return valueForInfantsUsingMaxTravelerCap;
    }
    return maxInfants;
  }, [
    adults,
    children,
    maxInfants,
    maxTravelersCap,
    maxCombinedChildrenAndInfants,
  ]);

  const { min, max } = useChildAgeRange();

  return (
    <div role="group" id="rooms-traveler-modal" className="margin-tb">
      {roomIndex > 0 && (
        <hr className="margin-b" data-testid="room-line-break" />
      )}
      {isMultiRoom && (
        <div className="margin-b flex flex-justify-between flex-align-center">
          <Heading level={2} variant="sans-xsmall-bold">
            {groupHeadingLabel}
          </Heading>
          {canRemove && (
            <RemoveRoomButton classNames="margin-l" roomIndex={roomIndex} />
          )}
        </div>
      )}
      <StepperRow
        className="margin-tb margin-1-t"
        defaultValue={DEFAULT_ADULT_TRAVELING}
        globalisedAria={globalisedAdultAria}
        isMultiRoom={isMultiRoom}
        max={maxAdultsForStepper}
        min={MIN_ADULT_TRAVELING}
        onChange={bindRoomIndexToUpdateTravelersUtil(roomIndex, updateAdults)}
        roomIndex={roomIndex}
        rowLabelId="adult"
        rowSubLabelId={hasAdultAgesHint ? 'adult_age_range' : null}
        rowSubLabelValues={{ min: Number.parseInt(max, 10) + 1 }}
        value={adults}
      />
      <hr className="margin-tb" />
      <StepperRow
        className="margin-tb"
        defaultValue={DEFAULT_CHILDREN_TRAVELING}
        globalisedAria={globalisedChildAria}
        isMultiRoom={isMultiRoom}
        max={maxChildrenForStepper}
        min={MIN_CHILDREN_TRAVELING}
        onChange={bindRoomIndexToUpdateTravelersUtil(roomIndex, updateChildCount)}
        roomIndex={roomIndex}
        rowLabelId="child"
        rowSubLabelId="child_age_range"
        rowSubLabelValues={{ min, max }}
        value={children.length}
      />
      {hasChildrenAges && children.length > 0 && (
        <ChildrenAges isMultiRoom={isMultiRoom} roomIndex={roomIndex} />
      )}
      {hasInfants && (
        <>
          <hr className="margin-tb" />
          <StepperRow
            className="margin-tb"
            defaultValue={DEFAULT_INFANTS_TRAVELING}
            globalisedAria={globalisedInfantAria}
            isMultiRoom={isMultiRoom}
            max={maxInfantsForStepper}
            min={MIN_INFANTS_TRAVELING}
            onChange={bindRoomIndexToUpdateTravelersUtil(roomIndex, updateInfantCount)}
            roomIndex={roomIndex}
            rowLabelId="infant"
            rowSubLabelId="age_under_2"
            value={infants.length}
          />
          {hasInfantsAges && infants.length > 0 && (
            <ChildAgeNote infants={infants}>
              <Infants isMultiRoom={isMultiRoom} roomIndex={roomIndex} />
            </ChildAgeNote>
          )}
        </>
      )}
      {!isMultiRoom && hasRooms && (
        <hr className="margin-2-tb" />
      )}
      {hasPets && (
        <>
          <hr className="margin-tb" />
          <StepperRow
            className="margin-tb"
            defaultValue={MIN_PETS}
            globalisedAria={formatMessage({ id: 'pets' })}
            isMultiRoom={false}
            max={MAX_PETS}
            min={MIN_PETS}
            onChange={bindRoomIndexToUpdateTravelersUtil(roomIndex, updatePets)}
            roomIndex={roomIndex}
            rowLabelId="pet"
            value={pets}
          />
          <span
            aria-live="off"
            className="dls-black legal-1"
            id="childAgeAtEndOfTravel"
          >
            {formatMessage({ id: 'pet_service_animals_notice' })}
          </span>
        </>
      )}
    </div>
  );
});

Room.displayName = 'Room';

Room.propTypes = {
  canRemove: PropTypes.bool,
  isMultiRoom: PropTypes.bool,
  roomIndex: PropTypes.number.isRequired,
};

Room.defaultProps = {
  canRemove: false,
  isMultiRoom: false,
};

export default Room;
